// Round Robin Tournament Utilities
//
// Round Robin format where every player plays every other player once.
// Supports 4-8 players.
// Winner is determined by most wins, with tiebreaker rules for ties.

/**
 * Build a round robin bracket where each player plays every other player once
 * @param {string[]} players - Array of player names
 * @returns {Object[]} Array of match objects
 */
export function buildRoundRobinBracket(players) {
  if (players.length < 4 || players.length > 8) {
    throw new Error('Round Robin format requires 4-8 players');
  }

  const matches = [];
  let matchId = 1;

  // Generate all possible pairings (each player plays each other once)
  const pairings = [];
  for (let i = 0; i < players.length; i++) {
    for (let j = i + 1; j < players.length; j++) {
      pairings.push([players[i], players[j]]);
    }
  }

  // Organize pairings into rounds using round-robin scheduling algorithm
  // This ensures no player plays more than once per round
  const rounds = organizePairingsIntoRounds(pairings, players.length);

  // Create match objects for each round
  rounds.forEach((roundPairings, roundIndex) => {
    roundPairings.forEach((pairing, matchIndex) => {
      matches.push({
        id: `RR-${matchId}`,
        matchNum: matchId,
        round: roundIndex + 1,
        bracket: 'RR', // Round Robin bracket type
        p1: { name: pairing[0] },
        p2: { name: pairing[1] },
        score1: '0',
        score2: '0',
        started: false,
        finished: false,
        winner: '',
        table: '',
        status: 'Pending'
      });
      matchId++;
    });
  });

  return matches;
}

/**
 * Organize pairings into rounds using round-robin scheduling
 * Ensures no player plays twice in the same round
 * @param {Array[]} pairings - Array of [player1, player2] pairings
 * @param {number} numPlayers - Total number of players
 * @returns {Array[]} Array of rounds, each containing pairings
 */
function organizePairingsIntoRounds(pairings, numPlayers) {
  const rounds = [];
  const remainingPairings = [...pairings];

  while (remainingPairings.length > 0) {
    const roundPairings = [];
    const playersInRound = new Set();

    // Try to add pairings to this round
    for (let i = remainingPairings.length - 1; i >= 0; i--) {
      const [p1, p2] = remainingPairings[i];

      // Check if both players are available (not already playing in this round)
      if (!playersInRound.has(p1) && !playersInRound.has(p2)) {
        roundPairings.push(remainingPairings[i]);
        playersInRound.add(p1);
        playersInRound.add(p2);
        remainingPairings.splice(i, 1);
      }
    }

    if (roundPairings.length > 0) {
      rounds.push(roundPairings);
    }
  }

  return rounds;
}

/**
 * Round Robin doesn't use BYE matches, so this is a no-op
 * @param {Object[]} bracket - Tournament bracket
 */
export function finalizeRoundRobinByeMatches(bracket) {
  // No BYE matches in Round Robin
  return;
}

/**
 * Propagate winners in Round Robin (no propagation needed, all matches are predetermined)
 * @param {Object[]} bracket - Tournament bracket
 */
export function propagateRoundRobinWinners(bracket) {
  // In Round Robin, there's no winner propagation - all matches are predetermined
  // Just ensure match statuses are correct
  return;
}

/**
 * Get standings for Round Robin tournament
 * Returns players sorted by: wins (descending), then losses (ascending), then alphabetically
 * @param {Object[]} bracket - Tournament bracket
 * @returns {Array[]} Array of player groups (for ties)
 */
export function getRoundRobinStandings(bracket) {
  if (!bracket || bracket.length === 0) {
    return [];
  }

  // Extract all unique players
  const players = new Set();
  bracket.forEach(match => {
    if (match.p1?.name && match.p1.name !== 'BYE') players.add(match.p1.name);
    if (match.p2?.name && match.p2.name !== 'BYE') players.add(match.p2.name);
  });

  // Calculate wins and losses for each player
  const playerStats = {};
  players.forEach(player => {
    playerStats[player] = { wins: 0, losses: 0, ties: 0 };
  });

  bracket.forEach(match => {
    if (match.finished && match.winner) {
      const p1Name = match.p1?.name;
      const p2Name = match.p2?.name;

      if (p1Name && p2Name && p1Name !== 'BYE' && p2Name !== 'BYE') {
        if (match.winner === p1Name) {
          playerStats[p1Name].wins++;
          playerStats[p2Name].losses++;
        } else if (match.winner === p2Name) {
          playerStats[p2Name].wins++;
          playerStats[p1Name].losses++;
        }
      }
    }
  });

  // Sort players by wins (desc), losses (asc), then name (asc)
  const sortedPlayers = Array.from(players).sort((a, b) => {
    const statsA = playerStats[a];
    const statsB = playerStats[b];

    // First sort by wins (descending)
    if (statsB.wins !== statsA.wins) {
      return statsB.wins - statsA.wins;
    }

    // Then by losses (ascending)
    if (statsA.losses !== statsB.losses) {
      return statsA.losses - statsB.losses;
    }

    // Finally alphabetically
    return a.localeCompare(b);
  });

  // Group players by same win/loss record (for tie detection)
  const standings = [];
  let currentGroup = [];
  let currentWins = null;
  let currentLosses = null;

  sortedPlayers.forEach(player => {
    const stats = playerStats[player];

    if (stats.wins === currentWins && stats.losses === currentLosses) {
      currentGroup.push(player);
    } else {
      if (currentGroup.length > 0) {
        standings.push(currentGroup);
      }
      currentGroup = [player];
      currentWins = stats.wins;
      currentLosses = stats.losses;
    }
  });

  if (currentGroup.length > 0) {
    standings.push(currentGroup);
  }

  return standings;
}

/**
 * Get player statistics for Round Robin
 * @param {Object[]} bracket - Tournament bracket
 * @returns {Object} Map of player names to their stats
 */
export function getRoundRobinPlayerStats(bracket) {
  const players = new Set();
  bracket.forEach(match => {
    if (match.p1?.name && match.p1.name !== 'BYE') players.add(match.p1.name);
    if (match.p2?.name && match.p2.name !== 'BYE') players.add(match.p2.name);
  });

  const playerStats = {};
  players.forEach(player => {
    playerStats[player] = {
      wins: 0,
      losses: 0,
      matchesPlayed: 0,
      matchesRemaining: 0
    };
  });

  bracket.forEach(match => {
    const p1Name = match.p1?.name;
    const p2Name = match.p2?.name;

    if (p1Name && p2Name && p1Name !== 'BYE' && p2Name !== 'BYE') {
      if (match.finished && match.winner) {
        playerStats[p1Name].matchesPlayed++;
        playerStats[p2Name].matchesPlayed++;

        if (match.winner === p1Name) {
          playerStats[p1Name].wins++;
          playerStats[p2Name].losses++;
        } else if (match.winner === p2Name) {
          playerStats[p2Name].wins++;
          playerStats[p1Name].losses++;
        }
      } else {
        playerStats[p1Name].matchesRemaining++;
        playerStats[p2Name].matchesRemaining++;
      }
    }
  });

  return playerStats;
}

/**
 * Check if Round Robin tournament is complete
 * @param {Object[]} bracket - Tournament bracket
 * @returns {boolean} True if all matches are finished
 */
export function isRoundRobinComplete(bracket) {
  if (!bracket || bracket.length === 0) return false;
  return bracket.every(match => match.finished);
}
